import {SignalData, kmsQueries} from '@domain/kiln-monitoring'
import {useQuery} from '@tanstack/react-query'
import React, {useContext, createContext, useMemo, useState, useEffect} from 'react'

interface ISensorsContext {
  sensorData: SignalData[] | undefined
  selectedIds: string[]
  selectedSensors: SignalData[]
  isLoading: boolean
  isError: boolean
  toggleSensorSelected?: (sensorId: string) => void
}

export const SensorsContext = createContext<ISensorsContext>(null as unknown as ISensorsContext)

export type SensorsProviderProps = {
  children: React.ReactNode
  kilnId?: string
  plantUpmId: string
  selectedIds?: string[]
  onToggleSensorSelected?: (sensorId: string) => void
}

export const SensorsProvider = ({
  children,
  kilnId,
  plantUpmId,
  selectedIds = [],
  onToggleSensorSelected
}: SensorsProviderProps) => {
  const {data: sensorData, isLoading, isError} = useQuery(kmsQueries.signals(kilnId, plantUpmId))

  const filteredSignalData = useMemo(
    () => (sensorData ? sensorData.map((sensor) => ({...sensor, lastReading: undefined})) : []),
    [sensorData]
  )

  const [signalData, setSignalData] = useState<SignalData[]>([])

  useEffect(() => {
    if (filteredSignalData && filteredSignalData.length) setSignalData(filteredSignalData)
  }, [filteredSignalData])

  const selectedSensors = useMemo(
    () =>
      Array.isArray(signalData)
        ? signalData?.filter((sensor) => selectedIds.includes(sensor.localName)) ?? []
        : [],
    [signalData, selectedIds]
  )

  return (
    <SensorsContext.Provider
      value={{
        sensorData: signalData,
        selectedIds,
        selectedSensors,
        isLoading,
        isError,
        toggleSensorSelected: onToggleSensorSelected
      }}
    >
      {children}
    </SensorsContext.Provider>
  )
}

export const useSensors = () => useContext(SensorsContext)
